/**
 * Zod validation schemas for role management operations
 *
 * Validates all external input for role management endpoints (DC-83).
 * Provides strict validation and sanitization of user input.
 *
 * References:
 * - spec.md FR-003: Role change validation
 * - spec.md FR-012: Optimistic locking with version numbers
 * - contracts/api-spec.yaml: Role management API specification
 */
import { z } from 'zod';
/**
 * Role enum schema
 *
 * Two fixed roles as defined in FR-001:
 * - admin: Full organization access (manage members, view audit logs, manage settings)
 * - member: Standard member access (view organization data)
 */
export const roleSchema = z.enum(['admin', 'member'], {
    errorMap: () => ({ message: 'Role must be one of: admin, member' }),
});
/**
 * UUID validation schema
 *
 * Validates UUID v4 format for organization and member IDs.
 */
export const uuidSchema = z.string().uuid('Invalid UUID format');
/**
 * Version number schema
 *
 * Validates version number for optimistic locking (FR-012).
 * Must be a non-negative integer.
 */
export const versionSchema = z
    .number()
    .int('Version must be an integer')
    .nonnegative('Version must be non-negative');
/**
 * Transfer ownership request schema
 *
 * Validates the request body for
 * POST /api/v1/organizations/:orgId/members/:memberId/transfer-ownership
 */
export const transferOwnershipSchema = z.object({
    version: versionSchema,
});
/**
 * Role change request schema (T024)
 *
 * Validates the request body for PATCH /api/v1/organizations/:orgId/members/:memberId/role
 * Implements optimistic locking with version field (FR-012).
 *
 * Example:
 * {
 *   "role": "admin",
 *   "version": 3
 * }
 */
export const roleChangeSchema = z.object({
    role: roleSchema,
    version: versionSchema,
});
/**
 * Alias for roleChangeSchema (T024)
 * Both names exported for consistency and backward compatibility.
 */
export const changeRoleSchema = roleChangeSchema;
/**
 * Member removal request schema (T035)
 *
 * Validates request body for DELETE /api/v1/organizations/:orgId/members/:memberId
 * This is an empty schema as the operation requires no body parameters.
 * All necessary information is in the URL path.
 *
 * Example: {} (empty body)
 */
export const removeMemberSchema = z.object({});
/**
 * Member removal request schema
 *
 * Validates parameters for DELETE /api/v1/organizations/:orgId/members/:memberId
 *
 * Note: This validates URL parameters, not request body.
 */
export const memberRemovalSchema = z.object({
    organizationId: uuidSchema,
    memberId: uuidSchema,
});
/**
 * List members query parameters schema
 *
 * Validates query parameters for GET /api/v1/organizations/:orgId/members
 *
 * Example: ?sort=name&order=asc
 */
export const listMembersQuerySchema = z.object({
    sort: z.enum(['name', 'email', 'joined_at']).optional().default('name'),
    order: z.enum(['asc', 'desc']).optional().default('asc'),
});
/**
 * Audit logs query parameters schema
 *
 * Validates query parameters for GET /api/v1/organizations/:orgId/audit-logs
 *
 * Example: ?action=role_change&limit=50&offset=0
 */
export const auditLogsQuerySchema = z.object({
    action: z.enum(['role_change', 'member_remove']).optional(),
    target_id: uuidSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
    offset: z.coerce.number().int().min(0).optional().default(0),
});
/**
 * User notifications query parameters schema
 *
 * Validates query parameters for GET /api/v1/users/me/notifications
 *
 * Example: ?unread_only=true&limit=20
 */
export const notificationsQuerySchema = z.object({
    unread_only: z.coerce.boolean().optional().default(false),
    limit: z.coerce.number().int().min(1).max(50).optional().default(20),
});
//# sourceMappingURL=role-management.js.map